/**
 * Validating and reading a user-supplied cover image.
 *
 * Pure-ish helpers over the File/FileReader APIs, kept out of the component for the
 * usual reason — a component that both validates and renders ends up with the rules
 * spread across three branches of JSX — and because the limits below are the sort of
 * thing that has to be assertable in a test.
 *
 * WHAT VALIDATION IS FOR HERE
 * ---------------------------
 * Not security. A data URL read in the browser and handed straight back to an <img>
 * cannot be made safe by checking its extension, and there is no server to protect.
 * These checks exist so the *user* gets told what went wrong instead of watching a
 * broken image appear: wrong format, too large, or a file the decoder rejects.
 *
 * When a real backend arrives it must validate independently — the client's checks
 * are a courtesy and are trivially bypassed. See api/coverApi.ts.
 */

import {
  ACCEPT_ATTRIBUTE,
  ACCEPTED_IMAGE_LABEL,
  ACCEPTED_IMAGE_TYPES,
  DEFAULT_IMAGE_UPLOAD_POLICY,
  MAX_IMAGE_BYTES,
  MAX_IMAGE_LABEL,
  formatBytes,
  type ImageRejection,
  type ImageUploadPolicy,
  validateImageFileLike,
} from './coverImagePolicy';

export {
  ACCEPTED_IMAGE_TYPES,
  ACCEPT_ATTRIBUTE,
  ACCEPTED_IMAGE_LABEL,
  MAX_IMAGE_BYTES,
  MAX_IMAGE_LABEL,
  formatBytes,
};
export type { ImageRejection, ImageUploadPolicy };

/**
 * Checks a file against the format and size rules.
 *
 * Returns null when the file is acceptable, or a rejection carrying the message to
 * show. The message is built here rather than in the component so the same wording
 * appears whether the file arrived by drop or by picker.
 */
export function validateImageFile(
  file: File,
  policy: ImageUploadPolicy = DEFAULT_IMAGE_UPLOAD_POLICY
): ImageRejection | null {
  return validateImageFileLike(file, policy);
}

/**
 * Reads a file into a data URL.
 *
 * Rejects on a read error, which is the "preview failures" case the brief asks
 * about: a file can pass the type and size checks and still be unreadable — a
 * truncated download, a file removed from disk between picking and reading, or an
 * image whose bytes do not decode. The caller turns that rejection into a message
 * rather than leaving an empty preview box.
 *
 * A data URL rather than URL.createObjectURL: the result is stored on the song
 * record, and an object URL is only valid until it is revoked or the document
 * unloads, so a reload would leave every uploaded cover pointing at nothing. The
 * tradeoff is size — see MAX_IMAGE_BYTES.
 */
export function readImageAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that image file.'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string' || !result.startsWith('data:image/')) {
        reject(new Error('That file could not be read as an image.'));
        return;
      }
      resolve(result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Picks the first acceptable image out of a drop.
 *
 * A drop can carry several files, or a directory, or none at all. Taking the first
 * item and validating it is what a single-image target should do; reporting "no
 * image here" is better than silently ignoring the gesture.
 */
export function firstImageFrom(list: FileList | null): File | null {
  if (!list || list.length === 0) return null;
  return list.item(0);
}
